import React from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import { useTheme } from '../../contexts/ThemeContext';
import { Button, Box } from '@mui/material';

const CustomDate = ({ value, onChange, label = "Select Date", }) => {
  const { theme } = useTheme();

  const CustomActionBar = () => {
    const handleClear = () => {
      onChange(null);
    };

    const handleToday = () => {
      onChange(dayjs());
    };

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '8px 16px',
          borderTop: `1px solid ${theme.text.primary}20`
        }}
      >
        <Button
          onClick={handleClear}
          sx={styles.footerButton}
        >
          Clear
        </Button>
        <Button
          onClick={handleToday}
          sx={styles.footerButton}
          variant="outlined"
          style={{
            marginLeft: '8px',
            borderColor: theme.color.primary,
            color: theme.color.primary
          }}
        >
          Today
        </Button>
      </Box>
    );
  };

  const styles = {
    datePicker: {
      '& .MuiOutlinedInput-root': {
        borderRadius: '5px',
        height: '40px',
        '& fieldset': {
          borderColor: value === "" ? theme.accent.error : theme.text.primary,
          borderRadius: '5px',
        },
        '&:hover fieldset': {
          borderColor: value === "" ? theme.accent.error : theme.text.primary,
          borderRadius: '5px',
        },
        '&.Mui-focused fieldset': {
          borderRadius: '5px',
          borderColor: value === "" ? theme.accent.error : theme.text.primary,
        },
        '& input': {
          fontFamily: theme.fontFamily,
          color: theme.text.primary,
          padding: '8px 14px',
        },
      },
      '& .MuiInputLabel-root': {
        color: value === "" ? theme.accent.error : theme.text.primary,
        fontFamily: theme.fontFamily,
        transform: 'translate(14px, 11px) scale(1)',
        '&.Mui-focused': {
          color: value === "" ? theme.accent.error : theme.text.primary,
        },
        '&.MuiInputLabel-shrink': {
          transform: 'translate(14px, -9px) scale(0.75)',
        },
      },
      '& .MuiInputAdornment-root .MuiSvgIcon-root': {
        color: value === "" ? theme.accent.error : theme.text.primary,
      },
      '& .MuiPaper-root': {
        borderRadius: '5px',
        backgroundColor: theme.background.primary,
      },
      '& .MuiPickersCalendarHeader-root': {
        fontFamily: theme.fontFamily,
        color: value === "" ? theme.accent.error : theme.text.primary,
        backgroundColor: theme.background.primary,
      },
      '& .MuiPickersCalendarHeader-labelContainer': {
        fontFamily: theme.fontFamily,
        color: value === "" ? theme.accent.error : theme.text.primary,
      },
      '& .MuiPickersCalendarHeader-switchViewButton': {
        fontFamily: theme.fontFamily,
        color: value === "" ? theme.accent.error : theme.text.primary,
        '&:hover': {
          backgroundColor: theme.color.secondary + '40',
        },
      },
      '& .MuiPickersDay-root': {
        fontFamily: theme.fontFamily,
        color: value === "" ? theme.accent.error : theme.text.primary,
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: theme.color.secondary + '40',
          color: value === "" ? theme.accent.error : theme.text.primary,
        },
        '&.Mui-selected': {
          backgroundColor: theme.color.primary,
          color: value === "" ? theme.accent.error : theme.background.primary,
          '&:hover': {
            backgroundColor: theme.color.primary,
            color: value === "" ? theme.accent.error : theme.background.primary,
          },
        },
        '&.MuiPickersDay-today': {
          border: `1px solid ${theme.color.primary}`,
          backgroundColor: 'transparent',
          color: value === "" ? theme.accent.error : theme.text.primary,
          '&:hover': {
            backgroundColor: theme.color.secondary + '40',
          },
        },
      },
      '& .MuiPickersDay-dayOutsideMonth': {
        color: value === "" ? theme.accent.error : theme.text.primary + '40',
      },
      '& .MuiDayCalendar-weekDayLabel': {
        color: value === "" ? theme.accent.error : theme.text.primary,
        fontWeight: 700,
        fontSize: '0.875rem',
        fontFamily: theme.fontFamily,
      },
      '& .MuiYearCalendar-root': {
        padding: '0 4px 16px 4px',
        margin: '0',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: theme.background.primary,
          borderRadius: '10px',
          margin: '8px 0',
        },
        '&::-webkit-scrollbar-thumb': {
          background: theme.color.primary,
          borderRadius: '10px',
          '&:hover': {
            background: theme.accent.secondary,
          },
        },
      },
      '& .MuiYearCalendar-root .MuiPickersYear-yearButton': {
        fontFamily: theme.fontFamily,
        color: value === "" ? theme.accent.error : theme.text.primary,
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: theme.color.secondary + '40',
        },
        '&.Mui-selected': {
          backgroundColor: theme.color.primary,
          color: value === "" ? theme.accent.error : theme.background.primary,
          '&:hover': {
            backgroundColor: theme.color.primary,
          },
        },
      },
      '& .MuiPickersYear-root': {
        color: value === "" ? theme.accent.error : theme.text.primary,
        fontFamily: theme.fontFamily,
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: theme.color.secondary + '40',
        },
        '&.Mui-selected': {
          backgroundColor: theme.color.primary,
          color: value === "" ? theme.accent.error : theme.background.primary,
          '&:hover': {
            backgroundColor: theme.color.primary,
          },
        },
      },
      '& .MuiPickersArrowSwitcher-root': {
        '& .MuiIconButton-root': {
          color: value === "" ? theme.accent.error : theme.text.primary,
          '&:hover': {
            backgroundColor: theme.color.secondary + '40',
          },
          '&.Mui-disabled': {
            color: value === "" ? theme.accent.error : theme.accent.disabled,
          },
        },
        '& .MuiSvgIcon-root': {
          color: value === "" ? theme.accent.error : theme.text.primary,
          fontSize: '1.5rem',
        },
      },
    },
    popper: {
      '& .MuiPaper-root': {
        borderRadius: '5px',
        backgroundColor: theme.background.primary,
        '& .MuiPickersLayout-root': {
          display: 'block',
        },
        '& .MuiYearCalendar-root': {
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: theme.scrollbar,
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.scrollbar,
            borderRadius: '10px',
            '&:hover': {
              background: theme.scrollbarHover,
            },
          },
        },
        '& .MuiPickersCalendarHeader-root': {
          backgroundColor: theme.background.primary,
        },
        '& .MuiPickersCalendarHeader-label': {
          color: theme.text.primary,
          fontFamily: theme.fontFamily,
        },
        '& .MuiPickersCalendarHeader-labelContainer': {
          color: theme.text.primary,
        },
        '& .MuiPickersCalendarHeader-switchViewButton': {
          color: theme.text.primary,
          '&:hover': {
            backgroundColor: theme.color.secondary + '40',
          },
        },
        '& .MuiPickersCalendarHeader-switchViewIcon': {
          color: theme.text.primary,
        },
        '& .MuiPickersDay-root': {
          fontFamily: theme.fontFamily,
          color: theme.text.primary,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: theme.color.secondary + '40',
            color: theme.text.primary,
          },
          '&.Mui-selected': {
            backgroundColor: theme.color.primary,
            color: theme.background.primary,
            '&:hover': {
              backgroundColor: theme.color.primary,
              color: theme.background.primary,
            },
          },
          '&.MuiPickersDay-today': {
            border: `1px solid ${theme.color.primary}`,
            backgroundColor: 'transparent',
            color: theme.text.primary,
            '&:hover': {
              backgroundColor: theme.color.secondary + '40',
            },
          },
        },
        '& .MuiPickersDay-dayOutsideMonth': {
          color: theme.text.primary + '40',
        },
        '& .MuiDayCalendar-weekDayLabel': {
          color: theme.text.primary,
          fontWeight: 700,
          fontSize: '0.875rem',
          fontFamily: theme.fontFamily,
        },
        '& .MuiYearCalendar-root .MuiPickersYear-yearButton': {
          fontFamily: theme.fontFamily,
          color: theme.text.primary,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: theme.color.secondary + '40',
          },
          '&.Mui-selected': {
            backgroundColor: theme.color.primary,
            color: theme.background.primary,
            '&:hover': {
              backgroundColor: theme.color.primary,
            },
          },
        },
        '& .MuiPickersArrowSwitcher-root': {
          '& .MuiIconButton-root': {
            color: theme.text.primary,
            '&:hover': {
              backgroundColor: theme.color.secondary + '40',
            },
            '&.Mui-disabled': {
              color: theme.accent.disabled,
            },
          },
          '& .MuiSvgIcon-root': {
            color: theme.text.primary,
            fontSize: '1.5rem',
          },
        },
      }
    },
    footerButton: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      textTransform: 'none',
      '&:hover': {
        backgroundColor: theme.color.secondary + '40',
      },
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label={label}
        value={value}
        onChange={onChange}
        minDate={dayjs()}
        maxDate={dayjs().add(10, 'year')}
        size="small"
        sx={styles.datePicker}
        slots={{
          actionBar: CustomActionBar
        }}
        slotProps={{
          actionBar: {
            actions: ['clear', 'today']
          },
          popper: {
            sx: styles.popper
          }
        }}
      />
    </LocalizationProvider>
  );
};

export default CustomDate;